import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import type { AppDispatch, RootState } from '../store';
import { RequestStatus } from '../utils';

/**
 * User self-ID interests (PTEC-569) — the opt-in mechanism behind Lead-Share
 * self-select audiences (PTEC-568). A user opts into interests from the shared
 * catalogue (LEAD_SHARE_INTEREST_CATALOGUE); a partner's `selfSelect.interestKeys`
 * match against these. Deny-by-default: no interests until the user opts in, so
 * self-select audiences reach only people who have raised their hand.
 *
 * Persisted per user, mirroring the preferences slice (`/users/self/...`).
 */
interface LeadShareInterestsState {
	interests: string[];
	status: RequestStatus;
	saveStatus: RequestStatus;
}

const initialState: LeadShareInterestsState = {
	interests: [],
	status: RequestStatus.IDLE,
	saveStatus: RequestStatus.IDLE
};

export const fetchUserInterests = createAsyncThunk<string[], void, { dispatch: AppDispatch; state: RootState }>(
	'leadShareInterests/fetch',
	async () => {
		const response = await API.get('inspirusapi', '/users/self/interests', {});
		return Array.isArray(response?.interests) ? response.interests : [];
	}
);

export const saveUserInterests = createAsyncThunk<string[], string[], { dispatch: AppDispatch; state: RootState }>(
	'leadShareInterests/save',
	async (interests) => {
		await API.put('inspirusapi', '/users/self/interests', { headers: {}, body: { interests } });
		return interests;
	}
);

const leadShareInterestsSlice = createSlice({
	name: 'leadShareInterests',
	initialState,
	reducers: {
		resetLeadShareInterests: () => initialState
	},
	extraReducers: (builder) => {
		builder.addCase(fetchUserInterests.pending, (state) => {
			state.status = RequestStatus.PENDING;
		});
		builder.addCase(fetchUserInterests.fulfilled, (state, action) => {
			state.interests = action.payload;
			state.status = RequestStatus.SUCCESS;
		});
		builder.addCase(fetchUserInterests.rejected, (state) => {
			state.status = RequestStatus.ERROR;
		});
		builder.addCase(saveUserInterests.pending, (state) => {
			state.saveStatus = RequestStatus.PENDING;
		});
		builder.addCase(saveUserInterests.fulfilled, (state, action) => {
			state.interests = action.payload;
			state.saveStatus = RequestStatus.SUCCESS;
		});
		builder.addCase(saveUserInterests.rejected, (state) => {
			state.saveStatus = RequestStatus.ERROR;
		});
	}
});

export const { resetLeadShareInterests } = leadShareInterestsSlice.actions;
export default leadShareInterestsSlice.reducer;
